import { Spinner, Tab, Nav, Tooltip } from 'react-bootstrap'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Button } from 'react-bootstrap'
import CustomModal from 'components/CustomModal/CustomModal'
import AnalyticsPaywall from '../AnalyticsPaywall/AnalyticsPaywall'
import AnalyticsAll from '../AnalyticsAll/AnalyticsAll'
import AnalyticsTracked from '../AnalyticsTracked/AnalyticsTracked'
import AnalyticsDownloads from '../AnalyticsDownloads/AnalyticsDownloads'
import AnalyticsPdf from '../AnalyticsPdf/AnalyticsPdf'

import styles from './AnalyticsModal.module.css'
import './Analytics.css'
import analyticsTypes from '../../../enums/analytics/analyticsTypes'
import { hideAnalyticsModal } from '../../../actions'
import { FILE_TYPE_PDF } from '../../../components/CreateUpdateSiteModal/interface'
import { exportAllAnalytics } from '../../../services/download-analytics'
import CustomTooltip from '../../CustomTooltip'

const sanitizeForFileName = (value = '') =>
  value
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

const AnalyticsModal = ({ trialMode, onUpgradeCTA }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const payload = useSelector((state) => state.modals.analyticsModalPayload)
  const [analyticsType, setAnalyticsType] = useState(payload?.analyticsType || analyticsTypes.ANALYTICS)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState('')

  useEffect(() => {
    history.push({ pathname: '/manage/analytics' })
  }, [history])

  const onClose = () => {
    const { pathname } = window.location
    if (pathname.startsWith('/manage/analytics')) {
      history.push('/manage')
    }
    dispatch(hideAnalyticsModal())
  }

  const handleSelectTab = (key) => {
    setAnalyticsType(key)
  }

  const handleExportAllAnalytics = async () => {
    if (!payload?.site?.subdomain || isExporting) return

    setExportError('')
    setIsExporting(true)

    try {
      const { data } = await exportAllAnalytics({ domain: payload.site.subdomain })
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `all-analytics-${sanitizeForFileName(payload.site.subdomain)}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)

      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export all analytics:', error)
      setExportError('Failed to export analytics. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <CustomModal
      show={true}
      size="xl"
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      enforceFocus={false}
      dialogClassName={styles.modalDialogWidth}
      mobileCloseButton={false}
    >
      <div className="p-0">
        {trialMode ? (
          <AnalyticsPaywall onUpgrade={(tab) => onUpgradeCTA(tab)} onClose={onClose} />
        ) : (
          <div className="p-3">
            {exportError && (
              <Alert variant="danger" className="mb-3">
                {exportError}
              </Alert>
            )}
            <Tab.Container
              activeKey={analyticsType}
              onSelect={handleSelectTab}
              id="analytics-tabs"
              mountOnEnter
              unmountOnExit
            >
              <div className={styles.tabsHeaderRow}>
                <Nav variant="pills" className={styles.analyticsTabs}>
                  <Nav.Item>
                    <Nav.Link eventKey={analyticsTypes.ANALYTICS}>Web analytics</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={analyticsTypes.TRACKED}>Tracked links</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={analyticsTypes.DOWNLOADS}>Downloads</Nav.Link>
                  </Nav.Item>
                  {payload?.site?.type === FILE_TYPE_PDF && payload?.site?.siteType === 'single' && (
                    <Nav.Item>
                      <Nav.Link eventKey={analyticsTypes.PDF}>PDF analytics</Nav.Link>
                    </Nav.Item>
                  )}
                </Nav>
                <div className={styles.modalActions}>
                  <CustomTooltip label="Export as Excel file">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={handleExportAllAnalytics}
                      disabled={!payload?.site?.subdomain || isExporting}
                    >
                      {isExporting ? (
                        <Spinner size="sm" />
                      ) : (
                        <img height="16" src="/assets/icons/download-file.svg" alt="Download" />
                      )}
                    </Button>
                  </CustomTooltip>
                </div>
              </div>

              <Tab.Content className="mt-4">
                <Tab.Pane eventKey={analyticsTypes.ANALYTICS}>
                  <AnalyticsAll domain={payload?.site} trialMode={trialMode} onUpgradeCTA={onUpgradeCTA} />
                </Tab.Pane>
                <Tab.Pane eventKey={analyticsTypes.TRACKED}>
                  <AnalyticsTracked key={payload?.site?.subdomain} domain={payload?.site} />
                </Tab.Pane>
                <Tab.Pane eventKey={analyticsTypes.DOWNLOADS}>
                  <AnalyticsDownloads domain={payload?.site} />
                </Tab.Pane>
                {payload?.site?.type === FILE_TYPE_PDF && payload?.site?.siteType === 'single' && (
                  <Tab.Pane eventKey={analyticsTypes.PDF}>
                    <AnalyticsPdf domain={payload?.site} />
                  </Tab.Pane>
                )}
              </Tab.Content>
            </Tab.Container>
          </div>
        )}
      </div>
    </CustomModal>
  )
}

export default AnalyticsModal
